import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      initialize: async () => {
        const token = get().token;
        if (!token) {
          set({ isLoading: false });
          return;
        }

        try {
          const response = await api.get('/auth/me');
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false
          });
        } catch (error) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      },

      login: async (email, password) => {
        try {
          set({ error: null });
          const response = await api.post('/auth/login', { email, password });
          const { token, user } = response.data;
          
          set({
            user,
            token,
            isAuthenticated: true,
            error: null
          });
          
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.error || 'Login failed';
          set({ error: message });
          return { success: false, error: message };
        }
      },

      register: async (email, password, referralCode = null) => {
        try {
          set({ error: null });
          const payload = { email, password };
          if (referralCode) payload.referralCode = referralCode;
          const response = await api.post('/auth/register', payload);
          const { token, user } = response.data;
          
          set({
            user,
            token,
            isAuthenticated: true,
            error: null
          });
          
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.error || 'Registration failed';
          set({ error: message });
          return { success: false, error: message };
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null
        });
      },

      updateUser: (userData) => {
        set({ user: { ...get().user, ...userData } });
      },

      refreshUser: async () => {
        try {
          const response = await api.get('/auth/me');
          set({ user: response.data });
          return response.data;
        } catch (error) {
          console.error('Failed to refresh user:', error);
          return null;
        }
      },

      connectWallet: async (walletData) => {
        try {
          const response = await api.post('/auth/wallet/connect', walletData);
          set({
            user: {
              ...get().user,
              wallet: response.data.wallet
            }
          });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.error || 'Failed to connect wallet' };
        }
      },

      disconnectWallet: async () => {
        try {
          await api.post('/auth/wallet/disconnect');
          set({
            user: {
              ...get().user,
              wallet: { ...get().user.wallet, isConnected: false }
            }
          });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.error || 'Failed to disconnect wallet' };
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token })
    }
  )
);

// Initialize auth on app load
useAuthStore.getState().initialize();
